import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner, TextInput } from "flowbite-react";
import Modal from "../common/AnimatedModal";
import { HiArrowLeft, HiSearch } from "react-icons/hi";
import axios from "../../config/axiosInstance";
import { CRM } from "../../config/apiConfig";
import TaskIcon from "../common/TaskIcon";

const SEARCH_DELAY = 300;
const CARDS_LIMIT = 40;

const toArray = (value) => (Array.isArray(value) ? value : []);

const cardLabel = (card) =>
  card?.title ||
  card?.lead?.name ||
  card?.lead?.companyName ||
  card?.leadName ||
  `Card ${card?.id || ""}`;

const VISIBILITY_LABEL = { PRIVATE: "Privado", TEAM: "Time", PUBLIC: "Público" };

// sem acento nem caixa, para "joao" achar "João"
const fold = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// todas as palavras digitadas precisam aparecer, em qualquer ordem
const matchesWords = (text, query) => {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = fold(text);
  return words.every((word) => haystack.includes(word));
};

/**
 * "Criar tarefa" (botão do funil e tecla T), em dois passos:
 *  1. escolher o FUNIL (pulado só quando quem abre já informa `funnel`);
 *  2. escolher o CARD desse funil (lista as primeiras páginas das colunas e
 *     busca no servidor ao digitar: título, contato, empresa, e-mail, telefone
 *     e responsáveis).
 * Tudo funciona pelo teclado, como na busca do ClickUp: digitar filtra, ↑/↓
 * movem o destaque e Enter escolhe o destacado (com uma única opção, é ela).
 * Escolher o card chama `onPick(cardId, funil)`; quem abre decide o resto
 * (abrir o modal de nova tarefa).
 */
export default function TaskCardPickerModal({ open, onClose, funnel, onPick }) {
  const [step, setStep] = useState("funnel");
  const [funnels, setFunnels] = useState([]);
  const [loadingFunnels, setLoadingFunnels] = useState(false);
  const [chosen, setChosen] = useState(null); // { id, name, stages }
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [settledQuery, setSettledQuery] = useState(null); // busca a que `rows` responde
  const [loadingCards, setLoadingCards] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const stageNamesRef = useRef({}); // funnelId -> { stageId: nome }
  const requestRef = useRef(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const pendingEnterRef = useRef(false); // Enter apertado antes da busca responder

  const enterCardStep = (next) => {
    setChosen({
      id: String(next.id),
      name: next.name || "Funil",
      stages: toArray(next.stages),
    });
    setQuery("");
    setRows([]);
    setSettledQuery(null);
    setActiveIndex(0);
    pendingEnterRef.current = false;
    setStep("card");
  };

  const goToFunnels = () => {
    setChosen(null);
    setQuery("");
    setRows([]);
    setSettledQuery(null);
    setActiveIndex(0);
    pendingEnterRef.current = false;
    setStep("funnel");
  };

  // Ao abrir: com funil conhecido vai direto aos cards; senão, escolhe o funil.
  useEffect(() => {
    if (!open) return;
    if (funnel?.id) {
      enterCardStep(funnel);
    } else {
      goToFunnels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, funnel]);

  // O campo é um só nos dois passos: devolve o foco a cada troca de passo.
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open, step]);

  // Passo 1: lista de funis
  useEffect(() => {
    if (!open || step !== "funnel") return undefined;
    let alive = true;
    setLoadingFunnels(true);
    axios
      .get(CRM.FUNNELS)
      .then((res) => {
        if (!alive) return;
        const list = toArray(res?.data?.data ?? res?.data);
        setFunnels(list);
      })
      .catch((err) => {
        console.error("Erro ao listar funis", err);
        if (alive) setFunnels([]);
      })
      .finally(() => alive && setLoadingFunnels(false));
    return () => {
      alive = false;
    };
  }, [open, step]);

  // Passo 2: cards do funil (busca no servidor, com atraso ao digitar)
  useEffect(() => {
    if (!open || step !== "card" || !chosen?.id) return undefined;
    const funnelId = chosen.id;
    const term = query.trim();
    const timer = window.setTimeout(
      async () => {
        const requestId = ++requestRef.current;
        setLoadingCards(true);
        try {
          if (!stageNamesRef.current[funnelId]) {
            const names = {};
            toArray(chosen.stages).forEach((st) => {
              if (st?.id) names[String(st.id)] = st.name;
            });
            if (!Object.keys(names).length) {
              const res = await axios.get(CRM.FUNNELS, {
                params: { expandedFunnelId: funnelId },
              });
              const found = toArray(res?.data?.data).find(
                (f) => String(f.id) === funnelId,
              );
              toArray(found?.stages).forEach((st) => {
                if (st?.id) names[String(st.id)] = st.name;
              });
            }
            stageNamesRef.current[funnelId] = names;
          }
          const res = await axios.get(CRM.BOARD_CARDS(funnelId), {
            params: {
              page: 1,
              limit: CARDS_LIMIT,
              filterSearch: term,
              // busca ampla: título, contato, empresa, e-mail, telefone e responsáveis
              ...(term ? { filterSearchAll: 1 } : {}),
            },
          });
          if (requestId !== requestRef.current) return;
          const byStage = res?.data?.data || {};
          const names = stageNamesRef.current[funnelId] || {};
          const next = [];
          Object.keys(byStage).forEach((stageId) => {
            toArray(byStage[stageId]?.cards).forEach((card) => {
              const title = cardLabel(card);
              const leadName = card?.lead?.name;
              const responsibles = toArray(card?.responsibles)
                .map((user) => user?.name)
                .filter(Boolean);
              if (!responsibles.length && card?.responsible?.name) {
                responsibles.push(card.responsible.name);
              }
              next.push({
                cardId: String(card.id),
                title,
                subtitle: [
                  leadName && leadName !== title ? leadName : null,
                  names[stageId],
                  responsibles.join(", "),
                ]
                  .filter(Boolean)
                  .join(" • "),
              });
            });
          });
          setRows(next);
          setActiveIndex(0);
        } catch (err) {
          console.error("Erro ao listar cards do funil", err);
          if (requestId === requestRef.current) setRows([]);
        } finally {
          if (requestId === requestRef.current) {
            setLoadingCards(false);
            setSettledQuery(query);
          }
        }
      },
      term ? SEARCH_DELAY : 0,
    );
    return () => window.clearTimeout(timer);
  }, [open, step, chosen, query]);

  const visibleFunnels = useMemo(
    () => funnels.filter((f) => matchesWords(f.name || "Funil", query)),
    [funnels, query],
  );
  const visibleRows = useMemo(() => rows.slice(0, CARDS_LIMIT * 2), [rows]);
  const items = step === "funnel" ? visibleFunnels : visibleRows;
  const safeIndex = items.length ? Math.min(activeIndex, items.length - 1) : -1;

  const pickCard = (row) => {
    if (row) onPick?.(row.cardId, chosen);
  };

  // Enter apertado enquanto a busca ainda não respondeu: escolhe o 1º da resposta
  useEffect(() => {
    if (!pendingEnterRef.current) return;
    if (step !== "card" || loadingCards || settledQuery !== query) return;
    pendingEnterRef.current = false;
    pickCard(visibleRows[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledQuery, loadingCards, visibleRows]);

  // mantém o item destacado à vista ao navegar pelo teclado
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [safeIndex, step, items.length]);

  const handleKeyDown = (event) => {
    if (event.nativeEvent?.isComposing) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!items.length) return;
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((safeIndex + delta + items.length) % items.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (step === "funnel") {
        if (visibleFunnels[safeIndex]) enterCardStep(visibleFunnels[safeIndex]);
      } else if (settledQuery === query && !loadingCards) {
        pickCard(visibleRows[safeIndex]);
      } else {
        pendingEnterRef.current = true;
      }
      return;
    }
    if (
      event.key === "Backspace" &&
      !event.repeat &&
      step === "card" &&
      !query
    ) {
      event.preventDefault();
      goToFunnels();
    }
  };

  const handleChange = (event) => {
    pendingEnterRef.current = false;
    setQuery(event.target.value);
    setActiveIndex(0);
  };

  const itemClass = (active) =>
    `flex w-full rounded-lg border px-3 py-2 text-left transition hover:border-orange-300 hover:bg-orange-50 ${
      active
        ? "border-orange-300 bg-orange-50"
        : "border-gray-200 bg-white"
    }`;

  return (
    <Modal dismissible show={open} onClose={onClose} size="lg">
      <Modal.Header className="py-3">
        <div className="flex items-center gap-2 text-base">
          <TaskIcon size={20} className="text-orange-600" />
          Criar tarefa
        </div>
      </Modal.Header>
      <Modal.Body className="space-y-3">
        {step === "funnel" ? (
          <p className="text-sm font-semibold text-gray-700">Escolha o funil</p>
        ) : (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <button
              type="button"
              onClick={goToFunnels}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold text-gray-600 hover:bg-gray-100"
              title="Trocar de funil"
            >
              <HiArrowLeft className="h-3.5 w-3.5" />
              Funis
            </button>
            <span className="truncate">
              Funil: <b className="text-gray-800">{chosen?.name}</b>
              {" · "}
              <span className="font-semibold text-gray-700">Escolha o card</span>
            </span>
          </div>
        )}
        <TextInput
          ref={inputRef}
          autoFocus
          icon={HiSearch}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            step === "funnel"
              ? "Buscar funil pelo nome"
              : "Buscar card por título, cliente, e-mail, telefone ou responsável"
          }
          sizing="sm"
          aria-controls="task-picker-list"
        />
        <div
          id="task-picker-list"
          role="listbox"
          ref={listRef}
          className="max-h-[50vh] space-y-1 overflow-y-auto pr-1"
        >
          {step === "funnel"
            ? visibleFunnels.map((f, index) => (
                <button
                  key={f.id}
                  id={`task-picker-item-${index}`}
                  role="option"
                  aria-selected={index === safeIndex}
                  data-active={index === safeIndex}
                  type="button"
                  tabIndex={-1}
                  onClick={() => enterCardStep(f)}
                  onMouseMove={() => setActiveIndex(index)}
                  className={`${itemClass(index === safeIndex)} items-center justify-between gap-3`}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {f.name || "Funil"}
                  </span>
                  {f.visibility ? (
                    <span className="text-[11px] text-gray-500">
                      {VISIBILITY_LABEL[String(f.visibility).toUpperCase()] ||
                        ""}
                    </span>
                  ) : null}
                </button>
              ))
            : visibleRows.map((row, index) => (
                <button
                  key={row.cardId}
                  id={`task-picker-item-${index}`}
                  role="option"
                  aria-selected={index === safeIndex}
                  data-active={index === safeIndex}
                  type="button"
                  tabIndex={-1}
                  onClick={() => pickCard(row)}
                  onMouseMove={() => setActiveIndex(index)}
                  className={`${itemClass(index === safeIndex)} flex-col`}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {row.title}
                  </span>
                  {row.subtitle ? (
                    <span className="text-xs text-gray-500">{row.subtitle}</span>
                  ) : null}
                </button>
              ))}
          {(step === "funnel" ? loadingFunnels : loadingCards) ? (
            <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-500">
              <Spinner size="sm" />
              {step === "funnel" ? "Carregando funis…" : "Buscando…"}
            </div>
          ) : null}
          {!items.length && !(step === "funnel" ? loadingFunnels : loadingCards) ? (
            <p className="py-6 text-center text-sm text-gray-500">
              {step === "funnel"
                ? query.trim()
                  ? "Nenhum funil encontrado."
                  : "Nenhum funil disponível."
                : query.trim()
                  ? "Nenhum card encontrado neste funil."
                  : "Este funil ainda não tem cards."}
            </p>
          ) : null}
        </div>
        <p className="text-[11px] text-gray-400">
          ↑ ↓ para navegar · Enter para escolher
          {step === "card" ? " · Backspace (campo vazio) volta aos funis" : ""}
          {" · Esc para fechar"}
        </p>
      </Modal.Body>
    </Modal>
  );
}
